import React, { useEffect, useId, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowPathIcon, ArrowsUpDownIcon, ArrowTrendingDownIcon, ArrowTrendingUpIcon, LightBulbIcon,
  PresentationChartLineIcon, UsersIcon,
} from '@heroicons/react/24/outline';
import FilterChip from '../FilterChip';
import { INSIGHT_STATUS, INSIGHT_TYPES, RAMP_METHOD_TEXT, insightDef } from '../../constants/insightsText';
import {
  RAMP_METHODS, selectInsightsEnabled, selectInsightsRampMethod, selectInsightsShowPast, selectInsightsStrongOnly,
  selectInsightsTypes, setInsightsEnabled, setInsightsRampMethod, setInsightsShowPast, setInsightsStrongOnly,
  setInsightsType,
} from '../../slices/preferencesSlice';
import {
  INSIGHTS_MAX_DAYS, selectInsightsAboutOpen, selectInsightsData, selectInsightsPastCutoff, selectInsightsStatus,
  setAboutOpen,
} from '../../slices/insightsSlice';
import { observedTally, prepareInsights, visiblePointers } from './prepare';

export const TYPE_ICONS = {
  ramp_down: ArrowTrendingDownIcon,
  ramp_up: ArrowTrendingUpIcon,
  split: ArrowsUpDownIcon,
  revision: ArrowPathIcon,
  crowd: UsersIcon,
  observed: PresentationChartLineIcon,
};

// Rows without a pointer count: the crowd is a hover view, observed ramps a tally.
const NO_COUNT = ['crowd', 'observed'];

const TYPE_TONE = {
  ramp_up: 'text-accent-600 dark:text-accent-400',
  default: 'text-brand-600 dark:text-brand-400',
};

const HEADING = 'px-2 py-1 text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-300';

function CheckRow({ type = 'checkbox', name, label, checked, disabled, onChange, describedBy, children }) {
  return (
    <label className={`flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 ${disabled ? 'opacity-50' : ''}`}>
      <input
        type={type}
        name={name}
        aria-label={label}
        aria-describedby={describedBy}
        className={`mt-0.5 ${type === 'checkbox' ? 'rounded ' : ''}border-gray-300 text-brand-600 focus:ring-brand-500`}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {children || <span className="text-gray-700 dark:text-gray-200">{label}</span>}
    </label>
  );
}

export default function InsightsMenu({ open, onToggle }) {
  const dispatch = useDispatch();
  const enabled = useSelector(selectInsightsEnabled);
  const strongOnly = useSelector(selectInsightsStrongOnly);
  const showPast = useSelector(selectInsightsShowPast);
  const types = useSelector(selectInsightsTypes);
  const rampMethod = useSelector(selectInsightsRampMethod);
  const data = useSelector(selectInsightsData);
  const aboutOpen = useSelector(selectInsightsAboutOpen);
  const pastCutoff = useSelector(selectInsightsPastCutoff);
  const status = useSelector(selectInsightsStatus);
  const baseId = useId();
  const ref = useRef(null);
  const aboutWasOpen = useRef(aboutOpen);
  const focusChip = () => ref.current?.querySelector('button')?.focus();

  // Escape closes the menu and hands focus back to the chip.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      onToggle();
      focusChip();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onToggle]);

  // The About panel opens from this menu, so its focus comes back here.
  useEffect(() => {
    if (aboutWasOpen.current && !aboutOpen) focusChip();
    aboutWasOpen.current = aboutOpen;
  }, [aboutOpen]);

  const pointers = useMemo(() => data?.pointers || [], [data]);
  // The same "past" cut-off as the chart's pins and labels (one per load).
  const cutoffSec = pastCutoff ?? undefined;
  const shown = useMemo(
    () => visiblePointers(pointers, { types, strongOnly, showPast, cutoffSec }),
    [pointers, types, strongOnly, showPast, cutoffSec],
  );
  // Lines follow the method of the loaded insights, else the one selected.
  const params = data?.params ?? { ramp_method: rampMethod };
  const countOf = (type) => visiblePointers(pointers, { strongOnly, showPast, cutoffSec }).filter((p) => p.type === type).length;
  // Observed ramps in the loaded range, once the layer is on and loaded under
  // a rule; warnings of the types shown.
  const tally = useMemo(() => {
    const prep = prepareInsights(data, { observed: types.observed !== false });
    if (!prep?.observed) return null;
    const t = observedTally(prep, types);
    return `${t.observed} observed · ${t.warned} warned`;
  }, [data, types]);

  return (
    <div ref={ref} className="relative">
      <FilterChip
        icon={LightBulbIcon}
        label="Insights"
        badge={enabled ? (status ? '-' : shown.length) : 'off'}
        active={open}
        onClick={onToggle}
      />
      {open && (
        <div role="dialog" aria-label="Insights" className="absolute right-0 z-50 mt-2 w-80 rounded-lg bg-white dark:bg-gray-870 border border-gray-200/70 dark:border-gray-600/70 shadow-lg p-1.5 text-sm">
          <CheckRow label="Show insights" checked={enabled} onChange={(on) => dispatch(setInsightsEnabled(on))}>
            <span className="font-semibold text-gray-900 dark:text-gray-100">Show insights</span>
          </CheckRow>
          {enabled && status && (
            <p role="status" className="px-2 pb-1 text-xs text-gray-500 dark:text-gray-300">
              {INSIGHT_STATUS[status](INSIGHTS_MAX_DAYS)}
            </p>
          )}
          <fieldset className="mt-1 border-t border-gray-200/70 dark:border-gray-600/70 pt-1" disabled={!enabled}>
            <legend className={`float-left w-full ${HEADING}`}>Ramps</legend>
            {RAMP_METHODS.map((method) => (
              <CheckRow
                key={method}
                type="radio"
                name={`${baseId}-ramps`}
                label={RAMP_METHOD_TEXT[method].name}
                checked={rampMethod === method}
                disabled={!enabled}
                onChange={() => dispatch(setInsightsRampMethod(method))}
                describedBy={`${baseId}-ramps-${method}`}
              >
                <span className="flex-1 min-w-0">
                  <span className="block text-gray-900 dark:text-gray-100">{RAMP_METHOD_TEXT[method].name}</span>
                  <span id={`${baseId}-ramps-${method}`} className="block text-xs text-gray-500 dark:text-gray-300">
                    {RAMP_METHOD_TEXT[method].hint}
                  </span>
                </span>
              </CheckRow>
            ))}
          </fieldset>
          <div className="mt-1 border-t border-gray-200/70 dark:border-gray-600/70 pt-1">
            <p className={HEADING}>Indicators</p>
            {INSIGHT_TYPES.map((type) => {
              const Icon = TYPE_ICONS[type];
              const def = insightDef(type, params);
              return (
                <CheckRow
                  key={type}
                  label={def.name}
                  checked={types[type] !== false}
                  disabled={!enabled}
                  onChange={(on) => dispatch(setInsightsType({ type, on }))}
                  describedBy={`${baseId}-${type}`}
                >
                  <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${TYPE_TONE[type] || TYPE_TONE.default}`} aria-hidden="true" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-gray-900 dark:text-gray-100">{def.name}</span>
                    <span id={`${baseId}-${type}`} className="block text-xs text-gray-500 dark:text-gray-300">{def.line}</span>
                    {type === 'observed' && tally && (
                      <span className="block text-xs text-gray-700 dark:text-gray-200 tabular-nums">{tally}</span>
                    )}
                  </span>
                  {!NO_COUNT.includes(type) && <span className="font-mono text-xs text-gray-500 dark:text-gray-300 tabular-nums">{countOf(type)}</span>}
                </CheckRow>
              );
            })}
          </div>
          <div className="mt-1 border-t border-gray-200/70 dark:border-gray-600/70 pt-1">
            <CheckRow label="Include past days" checked={showPast} disabled={!enabled} onChange={(on) => dispatch(setInsightsShowPast(on))} />
            <CheckRow label="Strong pointers only" checked={strongOnly} disabled={!enabled} onChange={(on) => dispatch(setInsightsStrongOnly(on))} />
          </div>
          <div className="mt-1 border-t border-gray-200/70 dark:border-gray-600/70 pt-1">
            <button
              type="button"
              className="w-full text-left px-2 py-1.5 rounded text-brand-600 dark:text-brand-300 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
              onClick={() => { dispatch(setAboutOpen(true)); onToggle(); }}
            >
              What do these mean? How are they calculated?
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
